import type { WeekDateRangeResponse } from '../api/generated';

/**
 * Returns the 1-based week index of the current week, or -1 if today falls
 * outside all week ranges. A week is "current" when today >= startDate and
 * today <= endDate.
 *
 * Accepts an optional `today` parameter for testability.
 */
export function findCurrentWeekIndex(
  weeks: WeekDateRangeResponse[],
  today: Date = new Date()
): number {
  const t = new Date(today);
  t.setHours(0, 0, 0, 0);

  for (let i = 0; i < weeks.length; i++) {
    const w = weeks[i];
    if (!w.startDate || !w.endDate) continue;
    const start = new Date(w.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(w.endDate);
    end.setHours(0, 0, 0, 0);
    if (t >= start && t <= end) return i + 1; // 1-based
  }

  return -1;
}

/**
 * A week (1-based index) is past when today > week.endDate, i.e. the full
 * week has elapsed. The current week is NOT past.
 */
export function isWeekPast(
  weekIndex: number,
  weeks: WeekDateRangeResponse[],
  today: Date = new Date()
): boolean {
  const week = weeks[weekIndex - 1];
  if (!week) return false;
  const t = new Date(today);
  t.setHours(0, 0, 0, 0);
  const end = new Date(week.endDate);
  end.setHours(0, 0, 0, 0);
  return t > end;
}

/**
 * A week has started when today >= week.startDate. Strictly wider than
 * `isWeekPast`: the current week has started but is not past. This is the
 * boundary at which a week's plan freezes — it mirrors the backend's
 * `IsoWeekDateRangeUtil.hasWeekStarted`, which rejects both plan edits and new
 * calculations from Monday onwards.
 *
 * Takes the week itself rather than an index because its callers iterate week
 * objects, while `isWeekPast` serves the Gantt's index-based column loop.
 */
export function hasWeekStarted(
  week: WeekDateRangeResponse,
  today: Date = new Date()
): boolean {
  const t = new Date(today);
  t.setHours(0, 0, 0, 0);
  const start = new Date(week.startDate);
  start.setHours(0, 0, 0, 0);
  return t >= start;
}
